<template>
  <v-dialog v-model="dialog" max-width="1000px" scrollable persistent>
    <v-card>
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-camera-document</v-icon>
        {{ $t('ocr_scorecard') || 'Cargar Scorecard' }}
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Step 1: Upload Image & Player Name -->
        <template v-if="currentStep === 'upload'">
          <v-row class="mb-6">
            <v-col cols="12">
              <h3 class="subtitle-2 mb-3">
                <v-icon small color="primary" class="mr-2">mdi-numeric-1-circle</v-icon>
                Carga de Imagen
              </h3>
              <div
                class="border-2 border-dashed rounded-lg pa-8 text-center cursor-pointer"
                :class="dragover ? 'bg-blue-lighten-5' : ''"
                @click="$refs.fileInput.click()"
                @dragover.prevent="dragover = true"
                @dragleave.prevent="dragover = false"
                @drop.prevent="handleDrop"
              >
                <v-icon size="48" color="primary" class="mb-2">mdi-cloud-upload-outline</v-icon>
                <div class="body-2 font-weight-bold mb-2">
                  {{ imageFile ? imageFile.name : 'Arrastra la imagen del scorecard aquí' }}
                </div>
                <div class="caption grey--text">
                  {{ imageFile ? 'Imagen cargada correctamente' : 'O haz clic para seleccionar un archivo' }}
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileSelect"
                />
              </div>
            </v-col>
          </v-row>

          <!-- Image Preview -->
          <v-row v-if="imagePreview" class="mb-6">
            <v-col cols="12">
              <div class="mb-3">
                <h3 class="subtitle-2 mb-3">
                  <v-icon small color="primary" class="mr-2">mdi-image-search</v-icon>
                  Preview de la Imagen
                </h3>
              </div>
              <div class="text-center">
                <img :src="imagePreview" style="max-width: 100%; max-height: 300px;" />
              </div>
            </v-col>
          </v-row>

          <!-- Player Names (up to 5) -->
          <v-row class="mb-6">
            <v-col cols="12">
              <h3 class="subtitle-2 mb-3">
                <v-icon small color="primary" class="mr-2">mdi-numeric-2-circle</v-icon>
                Nombres de los Socios (hasta 5)
              </h3>
              <v-alert type="info" dense outlined class="mb-4">
                Ingresa los nombres en orden. Cada línea del scorecard corresponderá a un socio.
              </v-alert>
              <v-row>
                <v-col v-for="(name, index) in playerNames" :key="`player-${index}`" cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="playerNames[index]"
                    :label="`Socio ${index + 1} ${index === 0 ? '*' : ''}`"
                    outlined
                    dense
                    :rules="index === 0 ? [v => !!v || 'Al menos el primer socio es requerido'] : []"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </template>

        <!-- Step 2: Processing -->
        <template v-if="currentStep === 'processing'">
          <v-row justify="center" class="py-12">
            <v-col cols="12" class="text-center">
              <v-progress-circular indeterminate color="primary" size="60" class="mb-4"></v-progress-circular>
              <div class="title font-weight-bold mb-2">Procesando imagen con OCR...</div>
              <div class="body-2 grey--text">Extrayendo datos del scorecard</div>
            </v-col>
          </v-row>
        </template>

        <!-- Step 3: Review & Edit Data (Multiple Players) -->
        <template v-if="currentStep === 'review'">
          <v-alert type="warning" icon="mdi-alert-circle-outline" outlined class="mb-6">
            <strong>Información importante:</strong> Los datos extraídos por OCR pueden contener errores.
            Por favor, verifica y corrige manualmente los golpes de cada hoyo antes de confirmar.
          </v-alert>

          <!-- Player Navigation Tabs -->
          <v-card class="mb-6" outlined>
            <v-tabs v-model="currentPlayerIndex" class="elevation-0">
              <v-tab v-for="(player, index) in extractedPlayers" :key="`tab-${index}`">
                <v-icon small class="mr-2">mdi-account-golf</v-icon>
                {{ player.player_name || `Socio ${index + 1}` }}
              </v-tab>
            </v-tabs>
          </v-card>

          <!-- Player Info Summary -->
          <v-card class="mb-6" outlined>
            <v-card-text>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="caption grey--text">Socio</div>
                  <div class="body-2 font-weight-bold">{{ extractedPlayers[currentPlayerIndex].player_name }}</div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="caption grey--text">Fecha</div>
                  <div class="body-2 font-weight-bold">{{ formatDate(new Date()) }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Extracted Data Tables -->
          <v-row class="mb-6">
            <v-col cols="12" md="6">
              <v-card flat>
                <v-card-title class="subtitle-2">Primeros 9 Hoyos (1-9)</v-card-title>
                <v-card-text>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th style="width: 50px;">Hoyo</th>
                          <th style="width: 50px;">Par</th>
                          <th style="width: 80px;">Golpes</th>
                          <th style="width: 60px;">+/-</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="hole in extractedPlayers[currentPlayerIndex].first_nine.holes" :key="`front-${hole.hole_number}`">
                          <td class="font-weight-bold text-center">{{ hole.hole_number }}</td>
                          <td class="text-center">{{ hole.par }}</td>
                          <td>
                            <v-text-field
                              v-model.number="hole.strokes"
                              type="number"
                              min="1"
                              dense
                              outlined
                              @input="updateScoreVsPar(hole, currentPlayerIndex)"
                              class="text-center"
                            ></v-text-field>
                          </td>
                          <td class="text-center font-weight-bold" :class="getScoreClass(hole.score_vs_par)">
                            {{ hole.score_vs_par > 0 ? '+' : '' }}{{ hole.score_vs_par }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-divider class="my-3"></v-divider>
                  <v-row dense class="pa-3">
                    <v-col cols="6" class="font-weight-bold">Par Total:</v-col>
                    <v-col cols="6" class="text-right font-weight-bold">{{ extractedPlayers[currentPlayerIndex].first_nine.par_total }}</v-col>
                    <v-col cols="6" class="font-weight-bold">Golpes:</v-col>
                    <v-col cols="6" class="text-right font-weight-bold primary--text">
                      {{ extractedPlayers[currentPlayerIndex].first_nine.strokes_total }}
                    </v-col>
                    <v-col cols="6" class="font-weight-bold">vs Par:</v-col>
                    <v-col cols="6" class="text-right font-weight-bold" :class="getScoreClass(extractedPlayers[currentPlayerIndex].first_nine.score_vs_par)">
                      {{ extractedPlayers[currentPlayerIndex].first_nine.score_vs_par > 0 ? '+' : '' }}{{ extractedPlayers[currentPlayerIndex].first_nine.score_vs_par }}
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card flat>
                <v-card-title class="subtitle-2">Segunda Vuelta (10-18)</v-card-title>
                <v-card-text>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th style="width: 50px;">Hoyo</th>
                          <th style="width: 50px;">Par</th>
                          <th style="width: 80px;">Golpes</th>
                          <th style="width: 60px;">+/-</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="hole in extractedPlayers[currentPlayerIndex].second_nine.holes" :key="`back-${hole.hole_number}`">
                          <td class="font-weight-bold text-center">{{ hole.hole_number }}</td>
                          <td class="text-center">{{ hole.par }}</td>
                          <td>
                            <v-text-field
                              v-model.number="hole.strokes"
                              type="number"
                              min="1"
                              dense
                              outlined
                              @input="updateScoreVsPar(hole, currentPlayerIndex)"
                              class="text-center"
                            ></v-text-field>
                          </td>
                          <td class="text-center font-weight-bold" :class="getScoreClass(hole.score_vs_par)">
                            {{ hole.score_vs_par > 0 ? '+' : '' }}{{ hole.score_vs_par }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-divider class="my-3"></v-divider>
                  <v-row dense class="pa-3">
                    <v-col cols="6" class="font-weight-bold">Par Total:</v-col>
                    <v-col cols="6" class="text-right font-weight-bold">{{ extractedPlayers[currentPlayerIndex].second_nine.par_total }}</v-col>
                    <v-col cols="6" class="font-weight-bold">Golpes:</v-col>
                    <v-col cols="6" class="text-right font-weight-bold primary--text">
                      {{ extractedPlayers[currentPlayerIndex].second_nine.strokes_total }}
                    </v-col>
                    <v-col cols="6" class="font-weight-bold">vs Par:</v-col>
                    <v-col cols="6" class="text-right font-weight-bold" :class="getScoreClass(extractedPlayers[currentPlayerIndex].second_nine.score_vs_par)">
                      {{ extractedPlayers[currentPlayerIndex].second_nine.score_vs_par > 0 ? '+' : '' }}{{ extractedPlayers[currentPlayerIndex].second_nine.score_vs_par }}
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Total Summary -->
          <v-card class="grey lighten-4 rounded-lg">
            <v-card-text class="py-5">
              <v-row>
                <v-col cols="12" sm="3" class="text-center">
                  <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">Par Total</div>
                  <div class="display-1 font-weight-bold grey--text text--darken-3">{{ extractedPlayers[currentPlayerIndex].summary.total_par }}</div>
                </v-col>
                <v-col cols="12" sm="3" class="text-center">
                  <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">Golpes</div>
                  <div class="display-1 font-weight-bold grey--text text--darken-3">{{ extractedPlayers[currentPlayerIndex].summary.total_strokes }}</div>
                </v-col>
                <v-col cols="12" sm="3" class="text-center">
                  <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">Net Score</div>
                  <div class="display-1 font-weight-bold primary--text">{{ extractedPlayers[currentPlayerIndex].summary.net_score }}</div>
                </v-col>
                <v-col cols="12" sm="3" class="text-center">
                  <div class="caption text-uppercase grey--text text--darken-1 font-weight-bold mb-1">vs Par</div>
                  <div class="display-1 font-weight-bold" :class="getScoreClass(extractedPlayers[currentPlayerIndex].summary.score_vs_par)">
                    {{ extractedPlayers[currentPlayerIndex].summary.score_vs_par > 0 ? '+' : '' }}{{ extractedPlayers[currentPlayerIndex].summary.score_vs_par }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="grey" @click="closeModal">
          {{ currentStep === 'upload' ? 'Cancelar' : 'Volver' }}
        </v-btn>
        <v-btn
          v-if="currentStep === 'upload'"
          color="primary"
          :disabled="!imageFile || !playerNames[0]"
          @click="simulateOcr"
        >
          Procesar Imagen
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-if="currentStep === 'review'"
          color="success"
          @click="confirmData"
        >
          <v-icon left>mdi-check</v-icon>
          Confirmar Scorecard
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'OcrScorecardModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      currentStep: 'upload',
      dragover: false,
      imageFile: null,
      imagePreview: null,
      playerNames: ['', '', '', '', ''],
      extractedPlayers: [],
      currentPlayerIndex: 0
    }
  },
  watch: {
    show (val) {
      this.dialog = val
    },
    dialog (val) {
      if (!val) {
        this.closeModal()
      }
    }
  },
  methods: {
    handleFileSelect (event) {
      const file = event.target.files[0]
      if (file && file.type.startsWith('image/')) {
        this.imageFile = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.imagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    handleDrop (event) {
      this.dragover = false
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.imageFile = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.imagePreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    async simulateOcr () {
      this.currentStep = 'processing'

      const formData = new FormData()
      formData.append('document', this.imageFile)
      formData.append('document_type', 'golf_card')
      formData.append('language', 'spa+eng')

      try {
        const apiUrl = process.env.VUE_APP_OCR_API_URL || 'http://localhost:8000'
        const apiKey = process.env.VUE_APP_OCR_API_KEY || 'your-api-key'

        const response = await fetch(`${apiUrl}/ocr/document/`, {
          method: 'POST',
          body: formData,
          headers: {
            'X-API-Key': apiKey
          }
        })

        const result = await response.json()

        if (!result.success) {
          const errorMsg = result.errors?.length > 0
            ? result.errors[0]
            : 'Error procesando scorecard con OCR'
          this.$emit('error', errorMsg)
          this.currentStep = 'upload'
          return
        }

        // Mapear respuesta OCR a jugadores
        const activePlayerNames = this.playerNames.filter(name => !!name)
        const ocrLines = result.extracted_data.lines || []
        const parValues = result.extracted_data.par_values || [4, 3, 4, 4, 4, 4, 5, 3, 5, 5, 3, 4, 4, 4, 3, 4, 4, 5]

        this.extractedPlayers = activePlayerNames.map((playerName, playerIndex) => {
          const lineData = ocrLines[playerIndex]

          if (!lineData) {
            console.warn(`No OCR data for player ${playerIndex + 1}: ${playerName}`)
            return this.getEmptyScorecard(playerName)
          }

          return this.buildScorecardFromOcr(playerName, lineData.strokes, parValues)
        })

        // Mostrar advertencias si las hay
        if (result.extracted_data.warnings && result.extracted_data.warnings.length > 0) {
          this.$emit('warning', result.extracted_data.warnings)
        }

        // Validar que se detectaron suficientes líneas
        if (ocrLines.length < activePlayerNames.length) {
          const missingCount = activePlayerNames.length - ocrLines.length
          const warning = `Solo se detectaron ${ocrLines.length} de ${activePlayerNames.length} líneas. Se crearán scorecards vacíos para los ${missingCount} faltante(s).`
          this.$emit('warning', [warning])
        }

        this.currentPlayerIndex = 0
        this.currentStep = 'review'
      } catch (error) {
        console.error('OCR Error:', error)
        this.$emit('error', `Error procesando imagen: ${error.message}`)
        this.currentStep = 'upload'
      }
    },
    buildScorecardFromOcr (playerName, strokes, parValues) {
      const data = {
        player_name: playerName,
        first_nine: { holes: [] },
        second_nine: { holes: [] }
      }

      // Validar que tenemos 18 golpes
      if (!Array.isArray(strokes) || strokes.length !== 18) {
        console.warn(`Invalid strokes data for ${playerName}:`, strokes)
        return this.getEmptyScorecard(playerName)
      }

      // Primeros 9 hoyos
      const frontNine = strokes.slice(0, 9).map((stroke, idx) => ({
        hole_number: idx + 1,
        par: parValues[idx],
        strokes: parseInt(stroke) || 0,
        score_vs_par: (parseInt(stroke) || 0) - parValues[idx],
        score_label: this.getScoreLabel((parseInt(stroke) || 0) - parValues[idx])
      }))

      // Segundos 9 hoyos
      const backNine = strokes.slice(9, 18).map((stroke, idx) => ({
        hole_number: idx + 10,
        par: parValues[idx + 9],
        strokes: parseInt(stroke) || 0,
        score_vs_par: (parseInt(stroke) || 0) - parValues[idx + 9],
        score_label: this.getScoreLabel((parseInt(stroke) || 0) - parValues[idx + 9])
      }))

      data.first_nine.holes = frontNine
      data.second_nine.holes = backNine

      // Calcular totales
      this.calculateTotals(data)

      return data
    },
    getScoreLabel (scoreVsPar) {
      if (scoreVsPar <= -2) return 'Eagle'
      if (scoreVsPar === -1) return 'Birdie'
      if (scoreVsPar === 0) return 'Par'
      if (scoreVsPar === 1) return 'Bogey'
      return 'Doble+'
    },
    getEmptyScorecard (playerName = '') {
      return {
        player_name: playerName,
        first_nine: {
          holes: Array.from({ length: 9 }, (_, i) => ({
            hole_number: i + 1,
            par: 4,
            strokes: 0,
            score_vs_par: -4,
            score_label: 'Par'
          })),
          par_total: 36,
          strokes_total: 0,
          score_vs_par: -36
        },
        second_nine: {
          holes: Array.from({ length: 9 }, (_, i) => ({
            hole_number: i + 10,
            par: 4,
            strokes: 0,
            score_vs_par: -4,
            score_label: 'Par'
          })),
          par_total: 36,
          strokes_total: 0,
          score_vs_par: -36
        },
        summary: {
          total_par: 72,
          total_strokes: 0,
          net_score: 0,
          score_vs_par: -72
        }
      }
    },
    calculateTotals (data) {
      // Primeros 9
      data.first_nine.par_total = data.first_nine.holes.reduce((sum, h) => sum + h.par, 0)
      data.first_nine.strokes_total = data.first_nine.holes.reduce((sum, h) => sum + h.strokes, 0)
      data.first_nine.score_vs_par = data.first_nine.strokes_total - data.first_nine.par_total

      // Segundos 9
      data.second_nine.par_total = data.second_nine.holes.reduce((sum, h) => sum + h.par, 0)
      data.second_nine.strokes_total = data.second_nine.holes.reduce((sum, h) => sum + h.strokes, 0)
      data.second_nine.score_vs_par = data.second_nine.strokes_total - data.second_nine.par_total

      // Totales
      data.summary.total_par = data.first_nine.par_total + data.second_nine.par_total
      data.summary.total_strokes = data.first_nine.strokes_total + data.second_nine.strokes_total
      data.summary.net_score = data.summary.total_strokes
      data.summary.score_vs_par = data.summary.total_strokes - data.summary.total_par
    },
    updateScoreVsPar (hole, playerIndex = 0) {
      hole.score_vs_par = hole.strokes - hole.par
      hole.score_label = this.getScoreLabel(hole.score_vs_par)
      this.calculateTotals(this.extractedPlayers[playerIndex])
    },
    getScoreClass (scoreVsPar) {
      if (scoreVsPar < -1) return 'primary--text'
      if (scoreVsPar === -1) return 'success--text'
      if (scoreVsPar === 0) return 'info--text'
      if (scoreVsPar === 1) return 'warning--text'
      return 'error--text'
    },
    formatDate (date) {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    confirmData () {
      this.$emit('save', {
        players: this.extractedPlayers,
        image: this.imageFile
      })
      this.closeModal()
    },
    closeModal () {
      this.dialog = false
      this.currentStep = 'upload'
      this.imageFile = null
      this.imagePreview = null
      this.playerNames = ['', '', '', '', '']
      this.extractedPlayers = []
      this.currentPlayerIndex = 0
      this.$emit('update:show', false)
    }
  }
}
</script>

<style scoped>
.border-2 {
  border-width: 2px;
}

.border-dashed {
  border-style: dashed;
}

.cursor-pointer {
  cursor: pointer;
}

.bg-blue-lighten-5 {
  background-color: rgba(33, 150, 243, 0.05);
}
</style>
